import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Animated Frying Pan SVG with rocking motion and rising steam sizzle
 */
const AnimatedFryingPan = ({ color = 'currentColor' }) => {
  return (
    <div className="relative w-5 h-5 flex items-center justify-center">
      {/* Rising Steam Lines */}
      <div className="absolute -top-1.5 left-1.5 flex gap-1 pointer-events-none">
        <motion.span
          animate={{ y: [0, -3, -5], opacity: [0, 0.9, 0] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }}
          className="w-0.5 h-1.5 rounded-full bg-current opacity-70"
        />
        <motion.span
          animate={{ y: [0, -3, -5], opacity: [0, 0.9, 0] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: 0.4, ease: 'easeOut' }}
          className="w-0.5 h-2 rounded-full bg-current opacity-70"
        />
        <motion.span
          animate={{ y: [0, -3, -5], opacity: [0, 0.9, 0] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: 0.8, ease: 'easeOut' }}
          className="w-0.5 h-1.5 rounded-full bg-current opacity-70"
        />
      </div>

      {/* Frying Pan with subtle sizzle rock */}
      <motion.svg
        animate={{ 
          rotate: [-7, 7, -7],
          y: [0, -1, 0]
        }}
        transition={{ 
          duration: 1.6, 
          repeat: Infinity, 
          ease: 'easeInOut' 
        }}
        width="18" 
        height="18" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2.2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className="shrink-0"
      >
        {/* Pan Body */}
        <ellipse cx="10" cy="14" rx="7" ry="4" />
        {/* Pan Rim */}
        <path d="M3 14c0 2.5 3.1 4.5 7 4.5s7-2 7-4.5" />
        {/* Pan Handle */}
        <path d="M16 12.5l5.5-3.5" strokeWidth="2.5" />
      </motion.svg>
    </div>
  );
};

/**
 * Animated Cooking Badge
 * - If count > 1: Alternates between animated frying pan (3s) and active count (2s)
 * - If count === 1: Shows lively animated frying pan continuously
 */
const AnimatedCookingBadge = ({ count = 1, themeColor = '#ff385c' }) => {
  const isMultiple = count > 1;
  const [showCount, setShowCount] = useState(false);

  useEffect(() => {
    if (!isMultiple) {
      setShowCount(false);
      return;
    }

    let timeoutId;
    const scheduleNext = (showingNumber) => {
      // 2000ms for count, 3200ms for pan animation
      const delay = showingNumber ? 2000 : 3200;
      timeoutId = setTimeout(() => {
        setShowCount((prev) => {
          const next = !prev;
          scheduleNext(next);
          return next;
        });
      }, delay);
    };

    scheduleNext(false);
    return () => clearTimeout(timeoutId);
  }, [isMultiple, count]);

  return (
    <div 
      className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm transition-all overflow-hidden relative shadow-2xs select-none"
      style={{ 
        backgroundColor: `${themeColor}18`, 
        color: themeColor 
      }}
    >
      <AnimatePresence mode="wait">
        {showCount && isMultiple ? (
          <motion.span
            key="count"
            initial={{ opacity: 0, scale: 0.75, y: 3 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.75, y: -3 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="font-black text-sm leading-none"
          >
            {count}
          </motion.span>
        ) : (
          <motion.div
            key="pan"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="flex items-center justify-center"
          >
            <AnimatedFryingPan color={themeColor} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnimatedCookingBadge;
